import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { apiClient } from '../../lib/api';
import { useAdminStores, useAdminGroups, useAdminRefetchAll } from '../../admin/context';

type Group = {
  id: string;
  name: string;
  slug: string;
  tenantCount: number;
  isActive?: boolean;
  parentGroup: { id: string; name: string; slug: string } | null;
};

type Feature = 'parts' | 'chemicals';

type Store = {
  id: string;
  slug: string;
  name: string;
  isActive: boolean;
  features: Feature[];
  group: { id: string; slug: string; name: string } | null;
  integrations: any[];
};

type GroupFormValues = {
  name: string;
  slug: string;
  parentGroupId?: string | null;
  isActive?: boolean;
};

type StoreFormValues = {
  name: string;
  slug: string;
  groupId: string | null;
  features: Feature[];
};

const GroupStoresManager: React.FC = () => {
  const { stores, loading: storesLoading, error: storesError } = useAdminStores();
  const { groups, loading: groupsLoading, error: groupsError } = useAdminGroups();
  const refetchAll = useAdminRefetchAll();
  const loading = storesLoading || groupsLoading;
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [showCreateStore, setShowCreateStore] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Show context errors
  useEffect(() => {
    const ctxError = storesError || groupsError;
    if (ctxError) setError(ctxError);
  }, [storesError, groupsError]);

  const storesByGroup = useMemo(() => {
    const map = new Map<string, Store[]>();
    stores.forEach((store) => {
      if (store.group) {
        if (!map.has(store.group.id)) {
          map.set(store.group.id, []);
        }
        map.get(store.group.id)!.push(store);
      }
    });
    return map;
  }, [stores]);

  const unassignedStores = useMemo(() => stores.filter((store) => !store.group), [stores]);

  function toggleGroup(groupId: string) {
    setExpandedGroups((previous) => ({
      ...previous,
      [groupId]: !previous[groupId],
    }));
  }

  async function handleCreateGroup(values: GroupFormValues) {
    setActionLoading('create-group');
    setError(null);
    setNotice(null);
    try {
      const response = await apiClient.post('/api/admin/groups', {
        name: values.name,
        slug: values.slug,
        parentGroupId: values.parentGroupId || undefined,
        metadata: {},
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to create group');
      }
      setShowCreateGroup(false);
      setNotice(`Created group ${values.name}`);
      await refetchAll();
    } catch (err: any) {
      setError(err.message || 'Failed to create group');
    }
    setActionLoading(null);
  }

  async function handleUpdateGroup(groupId: string, values: GroupFormValues) {
    setActionLoading(`group-${groupId}`);
    setError(null);
    setNotice(null);
    try {
      const payload: Record<string, any> = {
        name: values.name,
        slug: values.slug,
      };
      if (values.parentGroupId !== undefined) {
        payload.parentGroupId = values.parentGroupId;
      }
      if (values.isActive !== undefined) {
        payload.isActive = values.isActive;
      }
      const response = await apiClient.put(`/api/admin/groups/${groupId}`, payload);
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to update group');
      }
      setEditingGroup(null);
      setNotice(`Updated group ${values.name}`);
      await refetchAll();
    } catch (err: any) {
      setError(err.message || 'Failed to update group');
    }
    setActionLoading(null);
  }

  async function handleCreateStore(values: StoreFormValues) {
    if (!values.groupId) {
      setError('Select a group for the new store');
      return;
    }
    setActionLoading('create-store');
    setError(null);
    setNotice(null);
    try {
      const response = await apiClient.post('/api/admin/stores', {
        name: values.name,
        slug: values.slug,
        groupId: values.groupId,
        features: values.features,
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to create store');
      }
      setShowCreateStore(false);
      setNotice(`Created store ${values.name}`);
      await refetchAll();
    } catch (err: any) {
      setError(err.message || 'Failed to create store');
    }
    setActionLoading(null);
  }

  async function handleUpdateStore(storeId: string, values: StoreFormValues) {
    setActionLoading(`store-${storeId}`);
    setError(null);
    setNotice(null);
    try {
      const payload: Record<string, any> = {
        name: values.name,
        slug: values.slug,
        features: values.features,
      };
      payload.groupId = values.groupId || null;
      const response = await apiClient.put(`/api/admin/stores/${storeId}`, payload);
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to update store');
      }
      setEditingStore(null);
      setNotice(`Updated store ${values.name}`);
      await refetchAll();
    } catch (err: any) {
      setError(err.message || 'Failed to update store');
    }
    setActionLoading(null);
  }

  async function handleToggleStore(store: Store) {
    setActionLoading(`toggle-${store.id}`);
    setError(null);
    setNotice(null);
    try {
      const response = await apiClient.put(`/api/admin/stores/${store.id}`, {
        isActive: !store.isActive,
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to update store status');
      }
      setNotice(`${store.name} is now ${!store.isActive ? 'active' : 'inactive'}`);
      await refetchAll();
    } catch (err: any) {
      setError(err.message || 'Failed to toggle store status');
    }
    setActionLoading(null);
  }

  async function handleTestConnection(store: Store) {
    setActionLoading(`test-${store.id}`);
    setError(null);
    try {
      const response = await apiClient.post(`/api/admin/stores/${store.id}/provider/test`, {});
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Provider test failed');
      }
      const data = await response.json();
      alert(`Test results for ${store.name}:\n${JSON.stringify(data, null, 2)}`);
    } catch (err: any) {
      setError(err.message || 'Failed to test provider connection');
    }
    setActionLoading(null);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Groups ({groups.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateGroup(true)}
          >
            Create Group
          </Button>
        </Box>
        {loading ? (
          <Typography sx={{ mt: 2 }}>Loading groups...</Typography>
        ) : groups.length ? (
          <Stack spacing={2}>
            {groups.map((group) => {
              const expanded = expandedGroups[group.id];
              const groupStores = storesByGroup.get(group.id) || [];
              return (
                <Card key={group.id} variant="outlined">
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flex: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => toggleGroup(group.id)}
                          aria-label={expanded ? 'Collapse group' : 'Expand group'}
                        >
                          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {group.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {group.slug}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Stores: {group.tenantCount}
                          </Typography>
                          {group.parentGroup && (
                            <Typography variant="body2" color="text.secondary">
                              Parent: {group.parentGroup.name}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={group.isActive === false ? 'Inactive' : 'Active'}
                          size="small"
                          color={group.isActive === false ? 'default' : 'success'}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setEditingGroup(group)}
                          disabled={actionLoading === `group-${group.id}`}
                        >
                          Edit
                        </Button>
                      </Stack>
                    </Box>
                    <Collapse in={expanded}>
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        {groupStores.length ? (
                          <Stack spacing={1.5}>
                            {groupStores.map((store) => (
                              <Card
                                key={store.id}
                                variant="outlined"
                                sx={{ bgcolor: 'background.default' }}
                              >
                                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="subtitle2">{store.name}</Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        {store.slug}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        Status: {store.isActive ? 'Active' : 'Inactive'}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        Integrations:{' '}
                                        {store.integrations?.length
                                          ? store.integrations
                                              .map((integration: any) => integration.provider)
                                              .join(', ')
                                          : 'None'}
                                      </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleToggleStore(store)}
                                        disabled={actionLoading === `toggle-${store.id}`}
                                      >
                                        {store.isActive ? 'Deactivate' : 'Activate'}
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setEditingStore(store)}
                                        disabled={actionLoading === `store-${store.id}`}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleTestConnection(store)}
                                        disabled={actionLoading === `test-${store.id}`}
                                      >
                                        Test
                                      </Button>
                                    </Stack>
                                  </Box>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No stores associated with this group.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <Typography sx={{ mt: 2 }}>No groups available.</Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Stores ({stores.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateStore(true)}
          >
            Create Store
          </Button>
        </Box>
        {loading ? (
          <Typography sx={{ mt: 2 }}>Loading stores...</Typography>
        ) : unassignedStores.length ? (
          <Stack spacing={2}>
            {unassignedStores.map((store) => (
              <Card key={store.id} variant="outlined">
                <CardContent>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Box>
                      <Typography variant="h6">{store.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {store.slug}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Status: {store.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleToggleStore(store)}
                        disabled={actionLoading === `toggle-${store.id}`}
                      >
                        {store.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setEditingStore(store)}
                        disabled={actionLoading === `store-${store.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleTestConnection(store)}
                        disabled={actionLoading === `test-${store.id}`}
                      >
                        Test Provider
                      </Button>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <Typography sx={{ mt: 2 }}>All stores are assigned to groups.</Typography>
        )}
      </Paper>

      {showCreateGroup && (
        <GroupForm
          title="Create Group"
          submitLabel="Create"
          groups={groups}
          loading={actionLoading === 'create-group'}
          onSubmit={handleCreateGroup}
          onCancel={() => setShowCreateGroup(false)}
        />
      )}

      {editingGroup && (
        <GroupForm
          title="Edit Group"
          submitLabel="Save"
          groups={groups.filter((group) => group.id !== editingGroup.id)}
          loading={actionLoading === `group-${editingGroup.id}`}
          initialValues={{
            name: editingGroup.name,
            slug: editingGroup.slug,
            parentGroupId: editingGroup.parentGroup?.id ?? null,
            isActive: editingGroup.isActive ?? true,
          }}
          allowStatusToggle
          onSubmit={(values) => handleUpdateGroup(editingGroup.id, values)}
          onCancel={() => setEditingGroup(null)}
        />
      )}

      {showCreateStore && (
        <StoreForm
          title="Create Store"
          submitLabel="Create"
          groups={groups}
          loading={actionLoading === 'create-store'}
          onSubmit={handleCreateStore}
          onCancel={() => setShowCreateStore(false)}
        />
      )}

      {editingStore && (
        <StoreForm
          title="Edit Store"
          submitLabel="Save"
          groups={groups}
          loading={actionLoading === `store-${editingStore.id}`}
          initialValues={{
            name: editingStore.name,
            slug: editingStore.slug,
            groupId: editingStore.group?.id ?? null,
            features: editingStore.features ?? ['parts', 'chemicals'],
          }}
          onSubmit={(values) => handleUpdateStore(editingStore.id, values)}
          onCancel={() => setEditingStore(null)}
        />
      )}
    </Box>
  );
};

type GroupFormProps = {
  title: string;
  submitLabel: string;
  groups: Group[];
  loading: boolean;
  initialValues?: GroupFormValues;
  allowStatusToggle?: boolean;
  onSubmit: (values: GroupFormValues) => Promise<void> | void;
  onCancel: () => void;
};

const GroupForm: React.FC<GroupFormProps> = ({
  title,
  submitLabel,
  groups,
  loading,
  initialValues,
  allowStatusToggle,
  onSubmit,
  onCancel,
}) => {
  const [name, setName] = useState(initialValues?.name ?? '');
  const [slug, setSlug] = useState(initialValues?.slug ?? '');
  const [parentGroupId, setParentGroupId] = useState<string>(initialValues?.parentGroupId ?? '');
  const [isActive, setIsActive] = useState(initialValues?.isActive ?? true);
  const [localError, setLocalError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setLocalError(null);
    if (!name.trim() || !slug.trim()) {
      setLocalError('Name and slug are required');
      return;
    }
    try {
      await onSubmit({
        name: name.trim(),
        slug: slug.trim(),
        parentGroupId: parentGroupId ? parentGroupId : null,
        isActive: allowStatusToggle ? isActive : undefined,
      });
    } catch (err: any) {
      setLocalError(err.message || 'Failed to submit group form');
    }
  }

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {localError && <Alert severity="error">{localError}</Alert>}
            <TextField
              fullWidth
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
            <TextField
              fullWidth
              label="Slug"
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Parent Group</InputLabel>
              <Select
                value={parentGroupId}
                onChange={(event) => setParentGroupId(event.target.value)}
                label="Parent Group"
              >
                <MenuItem value="">None</MenuItem>
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {allowStatusToggle && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isActive}
                    onChange={(event) => setIsActive(event.target.checked)}
                  />
                }
                label="Group is active"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving…' : submitLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

type StoreFormProps = {
  title: string;
  submitLabel: string;
  groups: Group[];
  loading: boolean;
  initialValues?: StoreFormValues;
  onSubmit: (values: StoreFormValues) => Promise<void> | void;
  onCancel: () => void;
};

const StoreForm: React.FC<StoreFormProps> = ({
  title,
  submitLabel,
  groups,
  loading,
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [name, setName] = useState(initialValues?.name ?? '');
  const [slug, setSlug] = useState(initialValues?.slug ?? '');
  const [groupId, setGroupId] = useState<string>(initialValues?.groupId ?? groups[0]?.id ?? '');
  const [features, setFeatures] = useState<Feature[]>(
    initialValues?.features ?? ['parts', 'chemicals'],
  );
  const [localError, setLocalError] = useState<string | null>(null);

  const toggleFeature = (feature: Feature) => {
    setFeatures((prev) =>
      prev.includes(feature) ? prev.filter((f) => f !== feature) : [...prev, feature],
    );
  };

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setLocalError(null);
    if (!name.trim() || !slug.trim()) {
      setLocalError('Name and slug are required');
      return;
    }
    if (!groupId) {
      setLocalError('Select a group for this store');
      return;
    }
    if (features.length === 0) {
      setLocalError('Select at least one feature (Parts or Chemicals)');
      return;
    }
    try {
      await onSubmit({
        name: name.trim(),
        slug: slug.trim(),
        groupId: groupId === 'none' ? null : groupId,
        features,
      });
    } catch (err: any) {
      setLocalError(err.message || 'Failed to submit store form');
    }
  }

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {localError && <Alert severity="error">{localError}</Alert>}
            <TextField
              fullWidth
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
            <TextField
              fullWidth
              label="Slug"
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Group</InputLabel>
              <Select
                value={groupId}
                onChange={(event) => setGroupId(event.target.value)}
                label="Group"
              >
                <MenuItem value="none">Unassigned</MenuItem>
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Features
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Select at least one feature this store has access to.
              </Typography>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={features.includes('parts')}
                      onChange={() => toggleFeature('parts')}
                    />
                  }
                  label="Parts"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={features.includes('chemicals')}
                      onChange={() => toggleFeature('chemicals')}
                    />
                  }
                  label="Chemicals"
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving…' : submitLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GroupStoresManager;
